import { Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import { IPatientTreatment } from "../../../../../../@types/Treatments";
import { renderChartEmptyState } from "../../../../../../components/ChartEmptyState";
import { CustomModalChartTick } from "../../../../../../components/ModalChart/CustomModalChartTick/CustomModalChartTick";
import {
  centerPerDay,
  buildMonthlyAvg,
  getEmptyAllAxis,
  getGraphSeparators,
  NebulizerChartRow,
} from "../../../../../../utils/chartUtils";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import { DATE_FILTER_ALL, DateFilterOptionType } from "../CustomOptionsBar";
import { CustomNebulizerModalChartLegend } from "./CustomNebulizerModalChartLegend/CustomNebulizerModalChartLegend";
import { TooltipCss } from "./NebulizerModalChart.styles";

type NebulizerModalChartProps = {
  data: IPatientTreatment[];
  width: string | number;
  height: string | number;
  isPinnedGraph?: boolean;
  dateInterval: DateFilterOptionType;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  onlyGraph?: boolean;
  onClick?: (data: NebulizerChartRow) => void;
  isEmpty?: boolean;
  emptyMessage?: string;
};

export function NebulizerModalChart({
  data,
  width,
  height,
  dateInterval,
  isPinnedGraph,
  minDate,
  maxDate,
  onlyGraph,
  onClick,
  isEmpty,
  emptyMessage,
}: NebulizerModalChartProps) {
  const { t } = useTranslation();

  const {
    graphSeparators,
    dates,
    separatorCounter,
    tickFormat,
    viewType,
    isTickMultipleLine,
    interval,
  } = getGraphSeparators(
    dateInterval,
    getEmptyAllAxis(dateInterval, isEmpty)?.anchor ??
      (isEmpty ? dateInterval.startDate : minDate),
    getEmptyAllAxis(dateInterval, isEmpty)?.anchor ??
      (isEmpty ? dateInterval.endDate : maxDate)
  );

  const [hoveredBarId, setHoveredBarId] = useState<string | null>(null);
  const [selectedBarId, setSelecteddBarId] = useState<string | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState<React.ReactNode>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const anchorRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  let referenceTicks: number[] = [];

  const chartCardAllView = onlyGraph && dateInterval.id === DATE_FILTER_ALL;

  const treatmentData = useMemo(() => {
    if (!data) return [];

    const base = data.map((t) => ({
      ...t,
      timeValue: new Date(t.time).getTime(),
      totalTreatmentTimeNum: Number.isFinite(+t.totalTreatmentTime)
        ? +t.totalTreatmentTime
        : 0,
    }));

    if (interval === "7D" || interval === "1M" || interval === "2M") {
      return centerPerDay(base, interval);
    }

    if (interval === "6M") {
      return buildMonthlyAvg(
        data,
        dateInterval.startDate.valueOf(),
        dateInterval.endDate.valueOf(),
        4
      );
    }

    if (interval === "1Y") {
      return buildMonthlyAvg(
        data,
        dateInterval.startDate.valueOf(),
        dateInterval.endDate.valueOf(),
        2
      );
    }

    if (interval === "4Y" || interval === "4Y+") {
      return buildMonthlyAvg(
        data,
        dateInterval.startDate.valueOf(),
        dateInterval.endDate.valueOf(),
        6
      );
    }

    return base;
  }, [data, interval]);

  const refs: number[] = [
    ...graphSeparators,
    ...dates.filter((_, index) => index % separatorCounter === 0),
  ];

  referenceTicks = [...refs];

  const tickXFormatter = (value: string) => {
    if (!isEmpty && (!data || data.length === 0)) return "";

    if (
      graphSeparators.includes(Number(value)) ||
      referenceTicks.includes(Number(value))
    ) {
      if (tickFormat === "Q") {
        return (
          value &&
          `Q${dayjs(value).format(tickFormat)}, ${dayjs(value).format("YY")}`
        );
      }

      return value && dayjs(value).format(tickFormat);
    }

    return "";
  };

  const handleXDomain = () => {
    if (!dateInterval || !data) return [0, 1];

    const emptyAllAxis = getEmptyAllAxis(dateInterval, isEmpty);
    if (emptyAllAxis) return emptyAllAxis.domain;

    let { startDate, endDate } = dateInterval;

    if (interval === "48H" || interval === "24H") {
      startDate = startDate.add(-2, "hour");
      endDate = endDate.add(2, "hour");
    }

    if (interval === "7D" || interval === "2M") {
      startDate = startDate.add(-12, "hour");
    }

    if (minDate && maxDate && endDate.diff(startDate, "year") > 5) {
      startDate = minDate;
      endDate = maxDate;
    }

    if (endDate.diff(startDate, "months") > 11) {
      endDate = endDate.add(30, "day");
    }

    const needsPadding =
      interval === "7D" ||
      interval === "1M" ||
      interval === "2M" ||
      interval === "6M" ||
      interval === "1Y" ||
      interval === "4Y" ||
      interval === "4Y+";

    if (needsPadding) {
      const padding = 12 * 60 * 60 * 1000; // 12h padding on each side
      return [startDate.valueOf() - padding, endDate.valueOf() + padding];
    }

    return [startDate.valueOf(), endDate.valueOf()];
  };

  const handleMouseMove = (barData: any, _idx: number, e: React.MouseEvent) => {
    const { clientX, clientY } = e;

    setPos((prev) =>
      prev.left === clientX && prev.top === clientY
        ? prev
        : { left: clientX, top: clientY }
    );

    const payload = barData?.payload;
    const complete = payload?.doseStatus === 1;

    const isAverage = Boolean(barData?.isAverage && barData?.monthRangeLabel);
    const mutedText: React.CSSProperties = { fontSize: 12, color: "#D6D6D6" };

    const title = (
      <div
        className={TooltipCss()}
        style={{ alignItems: isAverage ? "flex-start" : undefined }}
      >
        <p>
          {t(
            complete
              ? "patientPage_measurementModal_nebulizer_doseComplete"
              : "patientPage_measurementModal_nebulizer_doseIncomplete"
          )}
        </p>

        {isAverage ? (
          <>
            <p style={mutedText}>{barData.monthRangeLabel}</p>
            {typeof barData.treatmentsCount === "number" && (
              <p style={mutedText}>
                {t("patientPage_measurementModal_nebulizer_treatmentCount", {
                  count: barData.treatmentsCount,
                })}
              </p>
            )}
          </>
        ) : (
          <p style={mutedText}>{getLongFormattedDate(payload?.time)}</p>
        )}
      </div>
    );

    setTooltipTitle(title);
    setTooltipOpen(true);
    setHoveredBarId(payload.id);
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
    setHoveredBarId(null);
  };

  const handleMouseClick = (barData: any) => {
    if (onlyGraph) return;

    onClick?.(barData);

    setSelecteddBarId(barData.id);
  };

  const handleBarOpacity = (id: string) => {
    const isSelected = selectedBarId === id;
    const isHovered = hoveredBarId === id;

    if (isSelected && isHovered) return 0.72;
    if (isSelected) return 0.72;
    if (isHovered) return 0.68;
    if (selectedBarId !== null) return 0.28;
    return 1;
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;

      if (!containerRef.current.contains(e.target as Node)) return;

      setSelecteddBarId(null);
    };

    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        paddingRight: "12px",
        minWidth: isEmpty ? 600 : undefined,
      }}
    >
      <div
        ref={anchorRef}
        style={{
          position: "fixed",
          left: pos.left,
          top: pos.top,
          pointerEvents: "none",
        }}
      />
      <Tooltip
        open={tooltipOpen}
        title={tooltipTitle}
        placement="top"
        zIndex={3000}
        arrow={false}
        styles={{
          body: {
            padding: "10px 20px",
            minHeight: "40px",
            width: "max-content",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Open Sans', sans-serif",
            fontSize: "12px !important",
            fontWeight: "500",
            backgroundColor: "#4b4b4b",
          },
        }}
        mouseEnterDelay={0.1}
      >
        <span
          style={{
            position: "fixed",
            left: pos.left,
            top: pos.top,
            width: 1,
            height: 1,
          }}
        />
      </Tooltip>

      <ResponsiveContainer
        debounce={10}
        width={width}
        height={height}
        minWidth={isPinnedGraph ? 658 : undefined}
      >
        <BarChart
          style={{
            width: "100%",
            maxHeight: "70vh",
            cursor: "pointer",
          }}
          data={treatmentData}
          margin={
            onlyGraph
              ? {
                  top: 5,
                  right: 5,
                  left: 5,
                  bottom: 5,
                }
              : {
                  top: 5,
                  right: 32,
                  left: 5,
                  bottom: 5,
                }
          }
        >
          {!onlyGraph ? (
            <CartesianGrid strokeDasharray="2" vertical={false} />
          ) : null}

          <XAxis
            tickMargin={14}
            stroke="#D8D8D8"
            type={chartCardAllView ? "category" : "number"}
            scale="time"
            ticks={viewType === "yearly" ? graphSeparators : dates}
            domain={
              chartCardAllView
                ? [minDate?.valueOf() ?? "auto", maxDate?.valueOf() ?? "auto"]
                : handleXDomain()
            }
            tickFormatter={tickXFormatter}
            allowDuplicatedCategory={false}
            tickLine={false}
            height={48}
            minTickGap={100}
            interval={0}
            hide={onlyGraph}
            dataKey="timeValue"
            tick={
              isTickMultipleLine ? (
                <CustomModalChartTick />
              ) : (
                {
                  fill: isEmpty ? "#B5B5B6" : "#727272",
                  fontSize: "12px",
                  fontWeight: "400",
                }
              )
            }
            angle={refs.length >= 15 ? 30 : 0}
          />

          <YAxis
            label={
              !onlyGraph
                ? {
                    value: `${
                      interval === "6M" ||
                      interval === "1Y" ||
                      interval === "4Y" ||
                      interval === "4Y+"
                        ? t(
                            "patientPage_measurementModal_nebulizer_averageDurationAxis"
                          )
                        : t(
                            "patientPage_measurementModal_nebulizer_durationAxis"
                          )
                    }`,
                    angle: -90,
                    position: "insideLeft",
                    dx: -5,
                    dy: 50,
                    style: {
                      fontSize: "12px",
                      color: "#727272CC",
                      marginLRight: 12,
                    },
                  }
                : undefined
            }
            tickMargin={8}
            stroke="#D8D8D8"
            domain={[0, 100]}
            tickCount={10}
            width={48}
            hide={onlyGraph}
            tick={{
              fill: isEmpty ? "#B5B5B6" : "#727272",
              fontSize: "12px",
              fontWeight: 400,
            }}
            allowDuplicatedCategory={false}
            allowDecimals={false}
          />

          {!onlyGraph
            ? refs.map((ref) => (
                <ReferenceLine
                  key={ref.toString()}
                  x={ref}
                  stroke="#D8D8D8"
                  strokeDasharray="8 1000"
                />
              ))
            : null}

          {!onlyGraph ? (
            <Legend
              content={
                <CustomNebulizerModalChartLegend
                  data={treatmentData}
                  startDate={
                    dateInterval.id === DATE_FILTER_ALL && minDate
                      ? minDate
                      : dateInterval.startDate
                  }
                  endDate={dateInterval.endDate}
                  latestTreatmentTime={maxDate}
                />
              }
            />
          ) : null}

          <Bar
            dataKey="totalTreatmentTimeNum"
            radius={[4, 4, 0, 0]}
            activeBar={false}
            isAnimationActive={false}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={() => {
              setSelecteddBarId(null);
            }}
          >
            {treatmentData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.doseStatus === 1 ? "#58C488" : "#F1801F"}
                opacity={handleBarOpacity(entry.id)}
                style={{ cursor: "pointer" }}
                width={
                  onlyGraph ||
                  interval === "2M" ||
                  interval === "1Y" ||
                  interval === "4Y" ||
                  interval === "4Y+"
                    ? 4
                    : 18
                }
                onClick={(e: React.MouseEvent) => {
                  e?.stopPropagation();
                  handleMouseClick(entry);
                }}
              />
            ))}
          </Bar>

          {isEmpty &&
            renderChartEmptyState({
              message: emptyMessage ?? t("chart_noDataForRange"),
            })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
